import React, {
  forwardRef,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Tabs,
  Button,
  message,
  Form,
  Modal,
  Row,
  Col,
  Typography,
  FloatButton,
  theme,
} from "antd";
import aiIcon from "../../../../assets/dashboardIcons/aiIcon.svg";
// import DoctorHistoryForm from "../../InspectionForms/HistoryForm/DoctorHistoryForm";
// import ClinicalImpressionForm from "../../InspectionForms/ClinicalImpressionForm/ClinicalImpressionForm";
// import GeneralExaminationForm from "../../InspectionForms/Examination/GeneralExaminationForm/GeneralExaminationForm";
// import SystemicExaminationForm from "../../InspectionForms/Examination/SystemicExaminationForm/SystemicExaminationForm";
// import FunctionalExaminationForm from "../../InspectionForms/Examination/FunctionalExaminationForm/FunctionalExaminationForm";

import "../../ipforms.css";
import "./InitialInspectionHeader.css";
import { DoctorInitialContext } from "../../InspectionForms/HistoryForm/DoctorHistoryFormProvider";
import {
  doctorInspectionAPI,
  getDoctorInspectionAPI,
  updateDoctorInspectionAPI,
} from "../../../../services/doctor.services";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import { useLocation } from "react-router-dom";
import { initialTabs } from "../../../../helpers/medicineKeys";
import FloatingRecorder from "./AiRecording/FloatingRecorder";
import useIsMobile from "../../../../hooks/useIsMobile";

const DoctorHistoryForm = lazy(() =>
  import("../../InspectionForms/HistoryForm/DoctorHistoryForm")
);
const ClinicalImpressionForm = lazy(() =>
  import("../../InspectionForms/ClinicalImpressionForm/ClinicalImpressionForm")
);
const GeneralExaminationForm = lazy(() =>
  import(
    "../../InspectionForms/Examination/GeneralExaminationForm/GeneralExaminationForm"
  )
);
const SystemicExaminationForm = lazy(() =>
  import(
    "../../InspectionForms/Examination/SystemicExaminationForm/SystemicExaminationForm"
  )
);
const FunctionalExaminationForm = lazy(() =>
  import(
    "../../InspectionForms/Examination/FunctionalExaminationForm/FunctionalExaminationForm"
  )
);

const InitialInspectionForm = forwardRef(
  (
    {
      refreshPage,
      editId,
      initialRefresh,
      setCurrent,
      setVisible,
      patientData,
      activeTab,
      setActiveTab,
    },
    ref
  ) => {
    const { token } = theme.useToken();
    const location = useLocation();
    const isMobile = useIsMobile();
    const params = Object.fromEntries(new URLSearchParams(location?.search));
    const [historyForm] = Form.useForm();
    const [generalForm] = Form.useForm();
    const [systemicForm] = Form.useForm();
    const [functionalForm] = Form.useForm();
    const [clinicalForm] = Form.useForm();
    const { formData, setFormData } = useContext(DoctorInitialContext);
    const tabItems = useMemo(
      () =>
        initialTabs.map((tab) =>
          isMobile && tab.key === "5"
            ? { ...tab, label: "Clinical Impression" }
            : tab
        ),
      [isMobile]
    );
    const [drainData, setDrainData] = useState(
      formData?.general_examination?.drain || formData?.drain || []
    );
    const [openWounds, setOpenWounds] = useState(
      formData?.systemic_examination?.open_wounds || formData?.open_wounds || []
    );
    const [pressureSore, setPressureSore] = useState(
      formData?.systemic_examination?.pressure_sore ||
        formData?.pressure_sore ||
        []
    );
    const [isDirty, setIsDirty] = useState(false);
    const [nextTab, setNextTab] = useState(null);
    const [buttonPressed, setButtonPressed] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [showFloatingRecorder, setShowFloatingRecorder] = useState(false);
    const formBodyRef = useRef(null);

    useEffect(() => {
      if (formBodyRef.current) {
        formBodyRef.current.scrollTop = 0;
      }
      // Keep the active tab visible in the mobile tab strip (esp. last tab)
      requestAnimationFrame(() => {
        document
          .querySelector(
            ".ia-form-tabs .ant-tabs-tab-active, .ia-form-tabs .ia-form-tabs-mobile-tab.is-active"
          )
          ?.scrollIntoView({
            inline: "nearest",
            block: "nearest",
            behavior: "smooth",
          });
      });
    }, [activeTab]);

    const handleSubmit = async (values, flag) => {
      try {
        let assessmentId = editId;
        if (editId) {
          await updateDoctorInspectionAPI(editId, values);
          initialRefresh();
        } else {
          const createRes = await doctorInspectionAPI(values);
          assessmentId =
            createRes?.data?.data?.id ||
            createRes?.data?.id ||
            params?.patient;
          refreshPage();
          initialRefresh();
        }

        message.success(`${flag} submitted successfully`);
        if (buttonPressed) {
          const nextTab = (parseInt(activeTab) + 1).toString();
          if (activeTab !== "5") {
            setActiveTab(nextTab);
          } else if (activeTab === "5" && formData?.care_items_generated) {
            setVisible(false);
            setActiveTab("1");
          } else {
            // Ensure Care Items step has the latest assessment payload
            const fetchId = assessmentId || editId || params?.patient;
            if (fetchId) {
              try {
                const response = await getDoctorInspectionAPI(fetchId);
                if (response?.data?.data) {
                  setFormData(response.data.data);
                }
              } catch (e) {
                console.log(e);
              }
            }
            setCurrent((prev) => prev + 1);
          }
          setButtonPressed(false);
        }
        setIsDirty(false);
        setShowFloatingRecorder(false);
      } catch (error) {
        message.error(error?.response?.data?.message || "Something went wrong");
      }
    };

    const onHistorySubmit = (values) => {
      handleSubmit(
        { history: values, admission_id: params?.patient },
        "History"
      );
    };
    const onGeneralSubmit = (values) => {
      handleSubmit(
        {
          general_examination: { ...values, drain: drainData },
          admission_id: params?.patient,
        },
        "General Examination"
      );
    };
    const onSystemicSubmit = (values) => {
      handleSubmit(
        {
          systemic_examination: {
            // ...formData?.systemic_examination,
            pressure_sore: pressureSore,
            open_wounds: openWounds,
            ...values,
          },
          admission_id: params?.patient,
        },
        "Systemic Examination"
      );
    };
    const onFunctionalSubmit = (values) => {
      handleSubmit(
        {
          functional_examination: values,
          admission_id: params?.patient,
        },
        "Functional Examination"
      );
    };
    const onClinicalSubmit = (values) => {
      handleSubmit(
        {
          clinical_diagnosis: values,
          admission_id: params?.patient,
          intial_assessment_done: true,
        },
        "Clinical Impression/Diagnosis"
      );
    };

    const getFormKey = () => {
      switch (activeTab) {
        case "1":
          return historyForm.submit();
        case "2":
          return generalForm.submit();
        case "3":
          return systemicForm.submit();
        case "4":
          return functionalForm.submit();
        case "5":
          return clinicalForm.submit();
        default:
          return "";
      }
    };
    const shouldShowFloatButton = () => {
      switch (activeTab) {
        case "1":
          return (
            !formData?.history ||
            (Object.keys(formData?.history || {}).length === 0 &&
              Object.keys(formData?.history?.treatment_history || {}).length ===
                0)
          );
        case "2":
          return (
            !formData?.general_examination ||
            Object.keys(formData?.general_examination || {}).length === 0
          );
        case "3":
          return (
            !formData?.systemic_examination ||
            Object.keys(formData?.systemic_examination || {}).length === 0
          );
        case "4":
          return (
            !formData?.functional_examination ||
            Object.keys(formData?.functional_examination || {}).length === 0
          );
        case "5":
          return (
            !formData?.clinical_diagnosis ||
            Object.keys(formData?.clinical_diagnosis || {}).length === 0
          );
        default:
          return false;
      }
    };

    const handleTabChange = (key) => {
      if (isDirty) {
        setNextTab(key);
        Modal.confirm({
          title: "Unsaved changes",
          content:
            "You have unsaved changes. Are you sure you want to save these changes?",
            okButtonProps: {
              style: {
                backgroundColor: token.colorPrimary,
                borderColor: token.colorPrimary,
              },
            },
          onOk: () => {
            switch (activeTab) {
              case "1":
                historyForm.validateFields().then((values) => {
                  setIsDirty(false);
                  onHistorySubmit(values);
                  setActiveTab(key);
                  setShowFloatingRecorder(false);
                });
                break;
              case "2":
                generalForm.validateFields().then((values) => {
                  setIsDirty(false);
                  onGeneralSubmit(values);
                  setActiveTab(key);
                  setShowFloatingRecorder(false);
                });
                break;
              case "3":
                systemicForm.validateFields().then((values) => {
                  setIsDirty(false);
                  onSystemicSubmit(values);
                  setActiveTab(key);
                  setShowFloatingRecorder(false);
                });
                break;
              case "4":
                functionalForm.validateFields().then((values) => {
                  setIsDirty(false);
                  onFunctionalSubmit(values);
                  setActiveTab(key);
                  setShowFloatingRecorder(false);
                });
                break;
              case "5":
                clinicalForm.validateFields().then((values) => {
                  setIsDirty(false);
                  onClinicalSubmit(values);
                  setActiveTab(key);
                  setShowFloatingRecorder(false);
                });
              default:
                setActiveTab(key);
            }
          },
          onCancel: () => {
            setActiveTab(key);
            setIsDirty(false);
            setShowFloatingRecorder(false);
          },
        });
      } else {
        setActiveTab(key);
        setShowFloatingRecorder(false);
      }
    };

    useImperativeHandle(ref, () => ({
      handleTabChange,
    }));

    const onFormValuesChange = () => {
      setIsDirty(true);
    };
    const handleProcessComplete = (responseData) => {
      setFormData(responseData);
      setPressureSore(responseData?.pressure_sore);
      setOpenWounds(responseData?.open_wounds);
      setDrainData(formData?.drain);
      setShowFloatingRecorder(false);
      setOpenModal(true);
    };

    return (
      <div className="initial-inspection-form-wrapper">
        <div className="ia-form-tabs">
          <Row>
            <Col
              xs={24}
              sm={24}
              md={{ span: 23, offset: 1 }}
              lg={{ span: 21, offset: 3 }}
              xl={{ span: 19, offset: 5 }}
              xxl={{ span: 18, offset: 6 }}
            >
              {isMobile ? (
                <div
                  className="ia-form-tabs-mobile-scroll"
                  role="tablist"
                  aria-label="Assessment sections"
                >
                  {tabItems.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.key}
                      className={`ia-form-tabs-mobile-tab${
                        activeTab === tab.key ? " is-active" : ""
                      }`}
                      onClick={() => handleTabChange(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              ) : (
                <Tabs
                  activeKey={activeTab}
                  items={tabItems}
                  onChange={handleTabChange}
                />
              )}
            </Col>
          </Row>
        </div>
        <div className="ia-form-panel">
          <div className="ia-form-body" ref={formBodyRef}>
            <Suspense fallback={<Typography>Loading...</Typography>}>
              {activeTab === "1" && (
                <Form
                  id="history-form"
                  form={historyForm}
                  layout="vertical"
                  onFinish={onHistorySubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms"
                >
                  <DoctorHistoryForm
                    form={historyForm}
                    context={DoctorInitialContext}
                    // editId={editId}
                    patientData={patientData}
                  />
                </Form>
              )}
              {activeTab === "2" && (
                <Form
                  id="general-form"
                  form={generalForm}
                  layout="vertical"
                  onFinish={onGeneralSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms"
                >
                  <GeneralExaminationForm
                    drainData={drainData}
                    setDrainData={setDrainData}
                    form={generalForm}
                  />
                </Form>
              )}
              {activeTab === "3" && (
                <Form
                  id="systemic-form"
                  form={systemicForm}
                  layout="vertical"
                  onFinish={onSystemicSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms"
                >
                  <SystemicExaminationForm
                    form={systemicForm}
                    pressureSore={pressureSore}
                    setPressureSore={setPressureSore}
                    openWounds={openWounds}
                    setOpenWounds={setOpenWounds}
                  />
                </Form>
              )}
              {activeTab === "4" && (
                <Form
                  id="functional-form"
                  form={functionalForm}
                  layout="vertical"
                  onFinish={onFunctionalSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms"
                >
                  <FunctionalExaminationForm form={functionalForm} />
                </Form>
              )}
              {activeTab === "5" && (
                <Form
                  id="clinical-form"
                  form={clinicalForm}
                  layout="vertical"
                  onFinish={onClinicalSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms"
                >
                  <ClinicalImpressionForm
                    form={clinicalForm}
                    context={DoctorInitialContext}
                    editId={editId}
                    patientData={patientData}
                  />
                </Form>
              )}
            </Suspense>
          </div>
          <div className="ia-form-footer">
            <Button
              onClick={() => {
                getFormKey();
                setButtonPressed(true);
                document
                  .querySelector(".initial-inspection-form-wrapper")
                  ?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
              }}
              type="primary"
              className={btnStyles.mdBtn}
              style={{ fontWeight: 500 }}
            >
              {activeTab === "5" ? "Done" : " Save & Proceed"}
            </Button>
          </div>
        </div>
        {shouldShowFloatButton() && (
          <FloatButton
            shape="circle"
            type="primary"
            // visibilityHeight={100}
            style={{ width: "64px", height: "64px", marginBottom: "10px" }}
            icon={<img src={aiIcon} alt="AI Icon" width={25} height={32} />}
            onClick={() => {
              setShowFloatingRecorder(true);
            }}
          />
        )}
        {showFloatingRecorder && (
          <FloatingRecorder
            onProcessComplete={handleProcessComplete}
            onClose={() => {
              setShowFloatingRecorder(false);
            }}
            audioFor={activeTab}
            // userDetails={userDetails}
          />
        )}
      </div>
    );
  }
);

export default InitialInspectionForm;
